// TimeEntriesCard — office view/correct surface for crew time entries
// (migration 0056; install_hours dropped in 0057 — CAPTURE ONLY, raw travel
// hours, no pay math). Mounted in JobDetailPage's right column.
//
// Collapsible <Card>: a table (Person | Date | Travel) with inline
// blur-commit editing (mirrors PhaseUI's PhaseInvoiceNumberField) + delete +
// an Add-entry row (PeopleMultiSelect single-person + date + travel hours).
// Plus an editable "Actual installed footage" field labeled LF/SF by
// product_line (the single per-job value, last-write-wins via the job PATCH).
//
// Crew self-report via the Telegram /hours flow; this is the office's
// correction/backfill surface. Office roles only (the API gates it server-side).

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Clock, Trash2, Plus } from 'lucide-react';
import type { JobStatus, ProductLine, TimeEntry } from '@gutterinv/shared';
import { Card } from './ui/card.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { PeopleMultiSelect } from './PeopleMultiSelect.js';
import {
  getJobTimeEntries,
  createJobTimeEntry,
  patchJobTimeEntry,
  deleteJobTimeEntry,
  getJobInstallAssignments,
  patchJobAssignmentFootage,
} from '../api/client.js';
import { qk } from '../lib/queryKeys.js';
import { footageUnitLabel, totalHours, formatHours } from '../lib/time-entry-format.js';
import { localISODate } from '../lib/hours-range.js';

// Office's LOCAL calendar date (not UTC — toISOString().slice() drifts to
// tomorrow after ~5pm ET). Shared with the Hours page via hours-range.ts.
function todayISO(): string {
  return localISODate();
}

export function TimeEntriesCard({
  jobId,
  productLine,
}: {
  jobId: string;
  jobStatus: JobStatus;
  productLine: ProductLine;
  actualInstalledFootage: number | null;
  /** Resolved quote footage (job.laborFootage) — kept for caller compat; the
   *  per-assignment editor now derives planned footage server-side per row. */
  quoteFootage: number;
}) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(true);
  const unit = footageUnitLabel(productLine);

  const { data: entries } = useQuery({
    queryKey: qk.jobs.timeEntries(jobId),
    queryFn: () => getJobTimeEntries(jobId),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: qk.jobs.timeEntries(jobId) });
    void queryClient.invalidateQueries({ queryKey: qk.jobs.installAssignments(jobId) });
    void queryClient.invalidateQueries({ queryKey: qk.jobs.detail(jobId) });
  };

  const list = entries ?? [];
  const totals = totalHours(list);

  return (
    <Card className="p-5">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-1.5 text-left"
      >
        <ChevronDown
          className={
            'h-3.5 w-3.5 text-muted-foreground transition-transform' +
            (expanded ? '' : ' -rotate-90')
          }
        />
        <h3 className="eyebrow flex items-center gap-1.5">
          <Clock className="h-3 w-3" />
          Crew Utilization
          {list.length > 0 && <span className="ml-1 text-muted-foreground">({list.length})</span>}
        </h3>
      </button>

      {expanded && (
        <div className="mt-3 space-y-4">
          {/* Actual installed footage — captured PER INSTALL ASSIGNMENT
              (migration 0065). Insulation = one field per phase; gutter = one
              (or, if date-segmented, more). Job total is the read-only sum. */}
          <InstallFootageEditor jobId={jobId} unit={unit} onSaved={invalidate} />

          {/* Entries table */}
          {list.length > 0 ? (
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full table-fixed text-sm">
                <colgroup>
                  <col />
                  <col className="w-32" />
                  <col className="w-24" />
                  <col className="w-12" />
                </colgroup>
                <thead>
                  <tr className="border-b border-border bg-secondary/30 text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2 font-medium">Person</th>
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium text-right">Travel</th>
                    <th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {list.map((e) => (
                    <TimeEntryRow key={e.id} jobId={jobId} entry={e} onChanged={invalidate} />
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border bg-secondary/20 font-medium">
                    <td className="px-3 py-2" colSpan={2}>
                      Total
                    </td>
                    <td className="tnum px-3 py-2 text-right">{formatHours(totals.travel)}</td>
                    <td className="px-3 py-2" />
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-secondary/20 py-6">
              <Clock className="h-6 w-6 text-taupe" />
              <span className="text-sm text-muted-foreground">No hours logged yet</span>
              <span className="text-xs text-taupe">Crew logs via /hours in Telegram</span>
            </div>
          )}

          {/* Add-entry */}
          <AddEntryForm jobId={jobId} existing={list} onAdded={invalidate} />
        </div>
      )}
    </Card>
  );
}

// ============================================================
// Actual installed footage — captured PER INSTALL ASSIGNMENT (migration 0065).
// One editable field per install assignment (insulation = per phase; gutter =
// per date-segment, usually one). Job total = read-only SUM of the captured
// actuals (mirrors the trigger-maintained jobs.actual_installed_footage rollup).
// PLANNED footage is a soft HINT shown as the field placeholder (NOT auto-saved
// — auto-filling planned-as-actual would fabricate per-phase precision the
// crew never measured; the office types the real number, referencing the hint).
// ============================================================
function InstallFootageEditor({
  jobId,
  unit,
  onSaved,
}: {
  jobId: string;
  unit: 'LF' | 'SF';
  onSaved: () => void;
}) {
  const { data: assignments } = useQuery({
    queryKey: qk.jobs.installAssignments(jobId),
    queryFn: () => getJobInstallAssignments(jobId),
  });
  const list = assignments ?? [];

  if (list.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-secondary/20 px-3 py-2 text-xs text-muted-foreground">
        No install assignment yet — footage is captured once the job is scheduled.
      </div>
    );
  }

  const captured = list.filter((a) => a.actualInstalledFootage !== null);
  const total = captured.reduce((sum, a) => sum + (a.actualInstalledFootage ?? 0), 0);
  const multi = list.length > 1;

  return (
    <div className="space-y-2">
      <div className="eyebrow flex items-center justify-between">
        <span>Actual installed ({unit})</span>
        <span className="tnum text-foreground">
          {captured.length > 0 ? `${Math.round(total * 100) / 100} ${unit}` : `— ${unit}`}
        </span>
      </div>
      <div className="overflow-hidden rounded-md border border-border">
        {list.map((a, i) => (
          <AssignmentFootageField
            key={a.id}
            jobId={jobId}
            assignmentId={a.id}
            label={
              a.phaseId !== null
                ? `Phase ${a.phaseNumber}`
                : multi
                  ? `Install ${i + 1}`
                  : 'Install'
            }
            unit={unit}
            planned={a.plannedFootage}
            actual={a.actualInstalledFootage}
            onSaved={onSaved}
          />
        ))}
      </div>
    </div>
  );
}

/** One install assignment's footage row — blur-commit, planned as placeholder
 *  hint, error-revert. Mirrors PhaseUI's PhaseInvoiceNumberField pattern. */
function AssignmentFootageField({
  jobId,
  assignmentId,
  label,
  unit,
  planned,
  actual,
  onSaved,
}: {
  jobId: string;
  assignmentId: string;
  label: string;
  unit: 'LF' | 'SF';
  planned: number;
  actual: number | null;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(actual !== null ? String(actual) : '');
  useEffect(() => {
    setDraft(actual !== null ? String(actual) : '');
  }, [actual]);

  const mutation = useMutation({
    mutationFn: (next: number | null) =>
      patchJobAssignmentFootage(jobId, assignmentId, { actualInstalledFootage: next }),
    onSettled: onSaved,
    onError: (err) => {
      setDraft(actual === null ? '' : String(actual));
      alert(err instanceof Error ? err.message : 'Failed to save footage');
    },
  });

  const commit = () => {
    const cleaned = draft.replace(/[^0-9.]/g, '');
    if (cleaned.trim() === '') {
      if (actual !== null) mutation.mutate(null); // clear back to uncaptured
      return;
    }
    const num = Number(cleaned);
    if (!Number.isFinite(num) || num < 0) {
      setDraft(actual === null ? '' : String(actual));
      return;
    }
    const rounded = Math.round(num * 100) / 100;
    if (rounded === actual) return; // no change
    mutation.mutate(rounded);
  };

  // Planned footage as a soft hint (placeholder) when nothing captured yet.
  const placeholder = actual === null && planned > 0 ? `plan ${Math.round(planned * 100) / 100}` : '—';

  return (
    <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1">
        <Input
          value={draft}
          inputMode="decimal"
          placeholder={placeholder}
          onChange={(ev) => setDraft(ev.target.value)}
          onBlur={commit}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') ev.currentTarget.blur();
          }}
          disabled={mutation.isPending}
          className="tnum h-8 w-24 text-right"
        />
        <span className="text-muted-foreground">{unit}</span>
      </div>
    </div>
  );
}

// ============================================================
// Editable entry row — inline blur-commit for travel hours.
// ============================================================
function TimeEntryRow({
  jobId,
  entry,
  onChanged,
}: {
  jobId: string;
  entry: TimeEntry;
  onChanged: () => void;
}) {
  const patchMutation = useMutation({
    mutationFn: (input: { travelHours?: number }) =>
      patchJobTimeEntry(jobId, entry.id, input),
    onSettled: onChanged,
    onError: (err) => alert(err instanceof Error ? err.message : 'Failed to save hours'),
  });
  const deleteMutation = useMutation({
    mutationFn: () => deleteJobTimeEntry(jobId, entry.id),
    onSettled: onChanged,
    onError: (err) => alert(err instanceof Error ? err.message : 'Failed to delete entry'),
  });

  const busy = patchMutation.isPending || deleteMutation.isPending;

  return (
    <tr className="border-b border-border last:border-0">
      <td className="truncate px-3 py-2 font-medium text-foreground">{entry.personName}</td>
      <td className="tnum whitespace-nowrap px-3 py-2 text-muted-foreground">{entry.workDate}</td>
      <td className="px-1 py-2 text-right">
        <HoursCell
          value={entry.travelHours}
          disabled={busy}
          onCommit={(v) => {
            if (v !== entry.travelHours) patchMutation.mutate({ travelHours: v });
          }}
        />
      </td>
      <td className="px-2 py-2 text-right">
        <button
          type="button"
          aria-label="Delete entry"
          disabled={busy}
          onClick={() => {
            if (window.confirm(`Delete ${entry.personName}'s hours for ${entry.workDate}?`)) {
              deleteMutation.mutate();
            }
          }}
          className="rounded-sm p-1 text-muted-foreground hover:text-destructive disabled:opacity-50"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}

/** Single hours cell with blur-commit + 0–24 clamp/validate. */
function HoursCell({
  value,
  disabled,
  onCommit,
}: {
  value: number;
  disabled?: boolean;
  onCommit: (v: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const cleaned = draft.replace(/[^0-9.]/g, '');
    const num = Number(cleaned);
    if (cleaned.trim() === '' || !Number.isFinite(num) || num < 0 || num > 24) {
      setDraft(String(value)); // revert invalid
      return;
    }
    onCommit(Math.round(num * 100) / 100);
  };

  return (
    <Input
      value={draft}
      inputMode="decimal"
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
      className="tnum h-8 w-full px-2 text-right"
    />
  );
}

// ============================================================
// Add-entry form — one person, a date, travel hours.
// ============================================================
function AddEntryForm({
  jobId,
  existing,
  onAdded,
}: {
  jobId: string;
  existing: TimeEntry[];
  onAdded: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [personIds, setPersonIds] = useState<string[]>([]);
  const [date, setDate] = useState(todayISO);
  const [travel, setTravel] = useState('0');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const crewMemberId = personIds[0]!;
      const t = Number(travel);
      return createJobTimeEntry(jobId, {
        crewMemberId,
        workDate: date,
        travelHours: Math.round(t * 100) / 100,
      });
    },
    onSuccess: () => {
      setOpen(false);
      setPersonIds([]);
      setDate(todayISO());
      setTravel('0');
      setError(null);
      onAdded();
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to add entry'),
  });

  const validHours = (s: string) => {
    const n = Number(s);
    return Number.isFinite(n) && n >= 0 && n <= 24;
  };
  const canSubmit =
    personIds.length === 1 &&
    /^\d{4}-\d{2}-\d{2}$/.test(date) &&
    validHours(travel) &&
    !mutation.isPending;

  // Block adding a person who already has an entry for the selected date
  // (the API rejects it as a duplicate; surface it pre-emptively).
  const dupForDate =
    personIds.length === 1 &&
    existing.some((e) => e.crewMemberId === personIds[0] && e.workDate === date);

  if (!open) {
    return (
      <Button variant="outline" size="sm" className="w-full" onClick={() => setOpen(true)}>
        <Plus className="h-4 w-4" />
        Add entry
      </Button>
    );
  }

  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <div>
        <label className="eyebrow mb-1.5 block">Person</label>
        {/* Single person per entry — `singleSelect` enforces one pick (replace
            on re-pick) and labels the input "Add person" / "Change person".
            Time entries are per-PERSON. */}
        <PeopleMultiSelect
          value={personIds}
          onChange={setPersonIds}
          singleSelect
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="eyebrow mb-1.5 block">Date</label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8" />
        </div>
        <div>
          <label className="eyebrow mb-1.5 block">Travel (h)</label>
          <Input
            value={travel}
            inputMode="decimal"
            onChange={(e) => setTravel(e.target.value)}
            className="tnum h-8 text-right"
          />
        </div>
      </div>
      {dupForDate && (
        <p className="text-xs text-destructive">
          This person already has an entry for that date — edit the row above instead.
        </p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex gap-2">
        <Button
          size="sm"
          className="flex-1"
          disabled={!canSubmit || dupForDate}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Adding…' : 'Add'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
